/* COLESICE theme: product page scripts (no libraries).
   <media-gallery>   swipe / thumbnails / dots, jumps to a variant's image, filters images by colour, zoom viewer
   <cs-variant-select>  finds the variant for the chosen options, updates the form and the address bar, re-renders
                     the variant dependent parts of the section ([data-part]) from the Section Rendering API
   <cs-buy-form>    add to cart through Theme.cart, shows errors under the buttons, gift card recipient fields
   <product-details>    keeps the sticky information column inside the screen
   <cs-share>    native share sheet or copy link
   Dispatches `variant:change` on document: { sectionId, variant, options }. */

const Theme = window.Theme || {};
const strings = (Theme.config && Theme.config.strings) || {};

const parseJSON = (text, fallback) => {
  try { return JSON.parse(text); } catch (e) { return fallback; }
};
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const isRtl = (el) => getComputedStyle(el).direction === 'rtl';

/* ---------------------------------------------------------------------------------------------
   Media gallery
--------------------------------------------------------------------------------------------- */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.maxZoom = Number(this.dataset.zoom) || 0;
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbList = this.querySelector('[data-gallery-thumbs]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    if (!this.track) return;

    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.syncActive());
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.onVariant = (event) => this.handleVariant(event.detail);
    document.addEventListener('variant:change', this.onVariant);
    this.resizeObserver = new ResizeObserver(() => this.syncActive());
    this.resizeObserver.observe(this.track);

    if (this.dialog) this.setupZoom();

    // Start on the media of the selected variant (the server marks it with aria-current)
    const start = this.querySelector('[data-gallery-goto][aria-current="true"]');
    const startItem = start ? this.itemById(start.dataset.mediaId) : null;
    requestAnimationFrame(() => {
      if (startItem && !startItem.hidden && startItem !== this.items[0]) this.goTo(startItem, false);
      this.syncActive();
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('variant:change', this.onVariant);
    this.resizeObserver?.disconnect();
  }

  get items() {
    return [...this.track.querySelectorAll('[data-gallery-item]')].filter((item) => !item.hidden);
  }

  itemById(id) {
    return this.track.querySelector(`[data-gallery-item][data-media-id="${id}"]`);
  }

  /* ----- navigation ----- */
  get scrollable() {
    return this.track.scrollWidth > this.track.clientWidth + 2;
  }

  goTo(item, smooth = true) {
    if (!item) return;
    const track = this.track;
    if (!this.scrollable) {
      if (smooth) item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      return;
    }
    const style = getComputedStyle(track);
    const pad = parseFloat(style.scrollPaddingInlineStart) || 0;
    const align = getComputedStyle(item).scrollSnapAlign || 'center';
    const left = Math.max(0, align.includes('start')
      ? item.offsetLeft - pad
      : item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2);
    track.scrollTo({ left: isRtl(track) ? -left : left, behavior: smooth ? 'smooth' : 'auto' });
  }

  step(direction) {
    const items = this.items;
    const index = items.indexOf(this.current);
    const next = items[clamp(index + direction, 0, items.length - 1)];
    if (next && next !== this.current) this.goTo(next);
  }

  syncActive() {
    const items = this.items;
    if (!items.length) return;
    let current = items[0];
    if (this.scrollable) {
      const track = this.track;
      const style = getComputedStyle(track);
      const pad = parseFloat(style.scrollPaddingInlineStart) || 0;
      const left = Math.abs(track.scrollLeft);
      let best = Infinity;
      items.forEach((item) => {
        const start = getComputedStyle(item).scrollSnapAlign.includes('start');
        const distance = start
          ? Math.abs(item.offsetLeft - pad - left)
          : Math.abs(item.offsetLeft + item.offsetWidth / 2 - (left + track.clientWidth / 2));
        if (distance < best) { best = distance; current = item; }
      });
    } else if (this.current && items.includes(this.current)) {
      current = this.current;
    }
    this.setCurrent(current);
  }

  setCurrent(item) {
    if (item === this.current) return;
    const previous = this.current;
    this.current = item;
    const id = item.dataset.mediaId;
    this.querySelectorAll('[data-gallery-goto]').forEach((button) => {
      const active = button.dataset.mediaId === id;
      button.setAttribute('aria-current', String(active));
      if (active && button.closest('[data-gallery-thumbs]')) this.revealThumb(button);
    });
    const items = this.items;
    const index = items.indexOf(item);
    this.querySelectorAll('[data-gallery-prev]').forEach((b) => (b.disabled = index <= 0));
    this.querySelectorAll('[data-gallery-next]').forEach((b) => (b.disabled = index >= items.length - 1));
    // stop a video that scrolled out of view
    previous?.querySelectorAll('video').forEach((video) => video.pause());
  }

  revealThumb(button) {
    const list = this.thumbList;
    if (!list) return;
    const vertical = list.scrollHeight > list.clientHeight + 2 && getComputedStyle(list).flexDirection === 'column';
    if (vertical) list.scrollTo({ top: button.offsetTop - (list.clientHeight - button.offsetHeight) / 2, behavior: 'smooth' });
    else if (list.scrollWidth > list.clientWidth + 2) list.scrollTo({ left: button.offsetLeft - (list.clientWidth - button.offsetWidth) / 2, behavior: 'smooth' });
  }

  /* ----- events ----- */
  onClick = (event) => {
    const goto = event.target.closest('[data-gallery-goto]');
    if (goto && this.contains(goto)) { this.goTo(this.itemById(goto.dataset.mediaId)); return; }
    if (event.target.closest('[data-gallery-prev]')) { this.step(-1); return; }
    if (event.target.closest('[data-gallery-next]')) { this.step(1); return; }
    if (event.target.closest('[data-gallery-zoom]')) { this.openZoom(this.current); return; }
    const zoomable = event.target.closest('.product-photos__item--zoomable');
    if (zoomable && event.target.matches('img')) this.openZoom(zoomable);
  };

  onKeydown = (event) => {
    if (event.target.closest('[data-gallery-dialog]')) return;
    if (!event.target.matches('[data-gallery-track], [data-gallery-goto]')) return;
    const dir = isRtl(this) ? -1 : 1;
    if (event.key === 'ArrowRight') { event.preventDefault(); this.step(dir); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-dir); }
  };

  /* ----- variant changes: filter by colour and jump to the variant's image ----- */
  handleVariant(detail) {
    if (!detail || detail.sectionId !== this.sectionId) return;
    const { variant, options } = detail;
    const mediaId = variant && variant.media ? String(variant.media) : null;
    const hiddenIds = new Set();
    this.track.querySelectorAll('[data-group-option]').forEach((item) => {
      const group = item.dataset.groupOption;
      const chosen = options ? options[group] : undefined;
      const hide = chosen !== undefined && chosen !== item.dataset.groupValue && item.dataset.mediaId !== mediaId;
      if (hide) hiddenIds.add(item.dataset.mediaId);
    });
    this.querySelectorAll('[data-media-id]').forEach((el) => { el.hidden = hiddenIds.has(el.dataset.mediaId); });
    const target = (mediaId && this.itemById(mediaId)) || null;
    this.current = null;
    if (target && !target.hidden) this.goTo(target, false);
    else if (hiddenIds.size) this.goTo(this.items[0], false);
    requestAnimationFrame(() => this.syncActive());
  }

  /* ----- zoom viewer ----- */
  zoomItems() {
    return this.items.filter((item) => item.dataset.zoomSrc);
  }

  openZoom(item) {
    if (!this.dialog || !this.maxZoom) return;
    const list = this.zoomItems();
    if (!list.length) return;
    const index = list.indexOf(item);
    this.showZoom(index < 0 ? 0 : index);
    Theme.openDialog(this.dialog);
  }

  showZoom(index) {
    const list = this.zoomItems();
    if (!list.length) return;
    this.zoomIndex = (index + list.length) % list.length;
    const item = list[this.zoomIndex];
    this.zoomImage.src = item.dataset.zoomSrc;
    this.zoomImage.alt = item.dataset.zoomAlt || '';
    const current = this.dialog.querySelector('[data-zoom-current]');
    const total = this.dialog.querySelector('[data-zoom-total]');
    if (current) current.textContent = String(this.zoomIndex + 1);
    if (total) total.textContent = String(list.length);
    const pager = this.dialog.querySelector('.product-zoom__pager');
    if (pager) pager.hidden = list.length < 2;
    this.resetZoom();
  }

  setupZoom() {
    const dialog = this.dialog;
    this.zoomStage = dialog.querySelector('[data-zoom-stage]');
    this.zoomImage = dialog.querySelector('[data-zoom-image]');
    this.zoom = { scale: 1, x: 0, y: 0 };
    const pointers = new Map();
    let gesture = null;

    const rect = () => this.zoomStage.getBoundingClientRect();
    const contentSize = () => {
      const r = rect();
      const w = this.zoomImage.naturalWidth || r.width;
      const h = this.zoomImage.naturalHeight || r.height;
      const fit = Math.min(r.width / w, r.height / h);
      return { w: w * fit, h: h * fit };
    };
    const apply = () => {
      const { scale, x, y } = this.zoom;
      this.zoomImage.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
      this.zoomStage.classList.toggle('is-zoomed', scale > 1.001);
    };
    const limit = () => {
      const r = rect();
      const size = contentSize();
      const maxX = Math.max(0, (size.w * this.zoom.scale - r.width) / 2);
      const maxY = Math.max(0, (size.h * this.zoom.scale - r.height) / 2);
      this.zoom.x = clamp(this.zoom.x, -maxX, maxX);
      this.zoom.y = clamp(this.zoom.y, -maxY, maxY);
    };
    this.resetZoom = () => { this.zoom = { scale: 1, x: 0, y: 0 }; apply(); };
    const zoomAt = (clientX, clientY, factor) => {
      const r = rect();
      const px = clientX - r.left - r.width / 2;
      const py = clientY - r.top - r.height / 2;
      const next = clamp(this.zoom.scale * factor, 1, Math.max(1, this.maxZoom));
      const f = next / this.zoom.scale;
      this.zoom.x = px - (px - this.zoom.x) * f;
      this.zoom.y = py - (py - this.zoom.y) * f;
      this.zoom.scale = next;
      if (next <= 1.001) { this.zoom.x = 0; this.zoom.y = 0; }
      limit();
      apply();
    };
    const toggleAt = (clientX, clientY) => {
      if (this.zoom.scale > 1.001) this.resetZoom();
      else zoomAt(clientX, clientY, this.maxZoom);
    };
    const distance = () => {
      const [a, b] = [...pointers.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const middle = () => {
      const [a, b] = [...pointers.values()];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };

    const stage = this.zoomStage;
    stage.addEventListener('pointerdown', (event) => {
      stage.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 1) {
        gesture = { startX: event.clientX, startY: event.clientY, originX: this.zoom.x, originY: this.zoom.y, time: Date.now(), moved: false, pinched: false };
      } else if (pointers.size === 2 && gesture) {
        gesture.pinched = true;
        gesture.distance = distance();
      }
    });
    stage.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId) || !gesture) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const now = distance();
        const mid = middle();
        if (gesture.distance) zoomAt(mid.x, mid.y, now / gesture.distance);
        gesture.distance = now;
        return;
      }
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;
      if (Math.abs(dx) + Math.abs(dy) > 6) gesture.moved = true;
      if (this.zoom.scale > 1.001 && !gesture.pinched) {
        this.zoom.x = gesture.originX + dx;
        this.zoom.y = gesture.originY + dy;
        limit();
        apply();
        stage.classList.add('is-dragging');
      }
    });
    const finish = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.delete(event.pointerId);
      stage.classList.remove('is-dragging');
      if (pointers.size || !gesture) return;
      const g = gesture;
      gesture = null;
      if (g.pinched) return;
      const dx = event.clientX - g.startX;
      const dy = event.clientY - g.startY;
      if (!g.moved && Date.now() - g.time < 500) toggleAt(event.clientX, event.clientY);
      else if (this.zoom.scale <= 1.001 && Math.abs(dx) > 60 && Math.abs(dy) < 80) this.showZoom(this.zoomIndex + (dx < 0 ? 1 : -1));
    };
    stage.addEventListener('pointerup', finish);
    stage.addEventListener('pointercancel', finish);
    stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      zoomAt(event.clientX, event.clientY, Math.exp(-event.deltaY * 0.0025));
    }, { passive: false });

    const center = () => { const r = rect(); return { x: r.left + r.width / 2, y: r.top + r.height / 2 }; };
    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-zoom-in]')) { const c = center(); zoomAt(c.x, c.y, 1.6); }
      else if (event.target.closest('[data-zoom-out]')) { const c = center(); zoomAt(c.x, c.y, 1 / 1.6); }
      else if (event.target.closest('[data-zoom-prev]')) this.showZoom(this.zoomIndex - 1);
      else if (event.target.closest('[data-zoom-next]')) this.showZoom(this.zoomIndex + 1);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.showZoom(this.zoomIndex + 1);
      else if (event.key === 'ArrowLeft') this.showZoom(this.zoomIndex - 1);
      else if (event.key === '+' || event.key === '=') { const c = center(); zoomAt(c.x, c.y, 1.4); }
      else if (event.key === '-') { const c = center(); zoomAt(c.x, c.y, 1 / 1.4); }
    });
    window.addEventListener('resize', () => { if (dialog.open) { limit(); apply(); } });
    dialog.addEventListener('close', () => {
      const item = this.zoomItems()[this.zoomIndex];
      if (item) this.goTo(item, false);
      this.resetZoom();
    });
  }
}
if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

/* ---------------------------------------------------------------------------------------------
   Variant picker
--------------------------------------------------------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.formId = this.dataset.formId;
    this.updateUrl = this.dataset.updateUrl === 'true';
    this.variants = parseJSON(this.querySelector('script[data-variants]')?.textContent, []);
    this.optionNames = parseJSON(this.dataset.optionNames, []);
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.controller?.abort();
  }

  get fieldsets() {
    return [...this.querySelectorAll('.variant-select__option')].sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
  }

  selectedValues() {
    return this.fieldsets.map((fieldset) => {
      const select = fieldset.querySelector('select');
      if (select) return select.value;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  onChange = (event) => {
    if (!event.target.matches('input[type="radio"], select')) return;
    const values = this.selectedValues();
    this.fieldsets.forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-option-value]');
      if (label && values[i] != null) label.textContent = values[i];
    });
    const variant = this.variants.find((v) => v.options.every((option, i) => option === values[i])) || null;

    const options = {};
    this.optionNames.forEach((name, i) => { if (values[i] != null) options[String(name).toLowerCase()] = String(values[i]).toLowerCase(); });

    const form = document.getElementById(this.formId);
    const idInput = form && form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.disabled = !variant;
      if (variant) idInput.value = variant.id;
    }
    this.showButtonState(form, variant);

    if (variant && this.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    Theme.emit('variant:change', { sectionId: this.sectionId, variant, options });
    if (variant) this.rerender(variant);
  };

  // Immediate feedback while the new markup is on its way
  showButtonState(form, variant) {
    const button = form && form.querySelector('[data-add-to-cart]');
    if (!button) return;
    const label = button.querySelector('span');
    if (!variant) {
      button.disabled = true;
      if (label) label.textContent = strings.unavailable || '';
    } else if (!variant.available) {
      button.disabled = true;
      if (label) label.textContent = strings.soldOut || '';
    } else {
      button.disabled = false;
      if (label) label.textContent = strings.addToCart || '';
    }
    form.querySelectorAll('.shopify-payment-button').forEach((el) => { el.hidden = !variant || !variant.available; });
  }

  async rerender(variant) {
    this.controller?.abort();
    const controller = (this.controller = new AbortController());
    try {
      const url = `${this.productUrl}?variant=${variant.id}&section_id=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const key = `section-${this.sectionId}`;
      const root = document.getElementById(key);
      const next = doc.getElementById(key);
      if (!root || !next) return;

      next.querySelectorAll('[data-part]').forEach((fresh) => {
        const current = root.querySelector(`[data-part="${CSS.escape(fresh.dataset.part)}"]`);
        if (current) current.innerHTML = fresh.innerHTML;
      });
      next.querySelectorAll('[data-option-flag]').forEach((fresh) => {
        const current = root.querySelector(`[data-option-flag="${CSS.escape(fresh.dataset.optionFlag)}"]`);
        if (!current) return;
        current.toggleAttribute('data-unavailable', fresh.hasAttribute('data-unavailable'));
        current.toggleAttribute('hidden', fresh.hasAttribute('hidden'));
        if (current.tagName === 'OPTION') current.textContent = fresh.textContent;
      });
      Theme.emit('product:updated', { sectionId: this.sectionId, variant });
    } catch (error) {
      if (error.name !== 'AbortError') console.warn(error);
    }
  }
}
if (!customElements.get('cs-variant-select')) customElements.define('cs-variant-select', VariantPicker);

/* ---------------------------------------------------------------------------------------------
   Add to cart form
--------------------------------------------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-form-error]');
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit);
    this.toggle = this.querySelector('[data-recipient-toggle]');
    if (this.toggle) this.toggle.addEventListener('change', this.onRecipientToggle);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.toggle?.removeEventListener('change', this.onRecipientToggle);
  }

  onRecipientToggle = () => {
    const fields = this.querySelector('[data-recipient-fields]');
    if (!fields) return;
    const on = this.toggle.checked;
    fields.hidden = !on;
    fields.querySelectorAll('input, textarea').forEach((el) => { el.disabled = !on; });
    const offset = fields.querySelector('[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());
  };

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message || '';
    this.error.hidden = !message;
  }

  onSubmit = async (event) => {
    event.preventDefault();
    const button = this.form.querySelector('[data-add-to-cart]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    if (!this.form.reportValidity()) return;
    if (!Theme.cart) { this.form.submit(); return; }

    this.showError('');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await Theme.cart.add(new FormData(this.form), 'cs-buy-form');
    } catch (error) {
      this.showError(error.message || strings.cartError);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };
}
if (!customElements.get('cs-buy-form')) customElements.define('cs-buy-form', ProductForm);

/* ---------------------------------------------------------------------------------------------
   Sticky information column: if it is taller than the screen, stick its bottom instead of its top
--------------------------------------------------------------------------------------------- */
class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.update = () => {
      if (!matchMedia('(min-width: 1000px)').matches) { this.style.removeProperty('--product-sticky-top'); return; }
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      // live: a column taller than the screen stops 20px above the bottom edge (fractional height, rounded)
      const fit = window.innerHeight - this.getBoundingClientRect().height - 20;
      this.style.setProperty('--product-sticky-top', `${Math.round(Math.min(header + 48, fit))}px`);
    };
    // "Apps below media" (e.g. Vitals bundles): live shows them under the media on desktop and, on phones, right after
    // the last accordion when more blocks follow it (e.g. "Find my size"); otherwise after the information.
    this.placeApps = () => {
      const apps = this.belowMedia;
      if (!apps) return;
      let anchor = null;
      if (!matchMedia('(min-width: 1000px)').matches) {
        const accordions = [...this.querySelectorAll(':scope > .product-details__block:has(> .product-details__accordion)')];
        const last = accordions[accordions.length - 1];
        if (last?.nextElementSibling) anchor = last;
      }
      if (anchor) {
        if (anchor.nextElementSibling !== apps) anchor.after(apps);
      } else if (this.nextElementSibling !== apps) {
        this.after(apps);
      }
      apps.classList.toggle('product__below-media--in-column', Boolean(anchor));
    };
    this.belowMedia = this.parentElement?.querySelector(':scope > .product__below-media') || this.belowMedia;
    this.placeApps();
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this);
    window.addEventListener('resize', this.update);
    window.addEventListener('resize', this.placeApps);
    window.addEventListener('load', this.update);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.update);
    window.removeEventListener('resize', this.placeApps);
    window.removeEventListener('load', this.update);
  }
}
if (!customElements.get('product-details')) customElements.define('product-details', ProductInfo);

/* ---------------------------------------------------------------------------------------------
   Share
--------------------------------------------------------------------------------------------- */
class ShareButton extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-share-trigger]');
    this.message = this.querySelector('.product-share__message');
    this.trigger?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onClick);
    clearTimeout(this.timer);
  }

  onClick = async () => {
    const data = { title: this.dataset.title, url: this.dataset.url };
    if (navigator.share) {
      try { await navigator.share(data); } catch (e) { /* closed by the shopper */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(data.url);
      if (this.message) {
        this.message.hidden = false;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { this.message.hidden = true; }, 2500);
      }
    } catch (e) { /* clipboard not available */ }
  };
}
if (!customElements.get('cs-share')) customElements.define('cs-share', ShareButton);
